import {
    MdCampaign,
    MdDirectionsRun,
    MdDirectionsWalk,
    MdMilitaryTech,
    MdSelfImprovement,
    MdSportsGymnastics,
    MdSportsScore,
} from "react-icons/md";

// Ready-made PT sound / tune sequences.
//
// Each entry is a short percussion cadence built from the synthesised voices in
// ptAudio.js. A pattern is a map of instrument -> step array, where every value
// is 0 (silent), 1 (normal hit) or 2 (accented downbeat). `steps` is the number
// of beats in one cycle and `defaultReps` how many cycles the drill runs by
// default. Everything (tempo, reps) is tweakable live in the player, so these
// act as interactive starting points rather than fixed tracks.
//
// This is offered alongside the routine builder ("My Routines") as a quick,
// zero-setup way to run a PT drill.

export const SEQUENCE_TYPES = [
    {value: "MARCH", label: "March", color: "blue"},
    {value: "CARDIO", label: "Cardio", color: "red"},
    {value: "SIGNAL", label: "Signal", color: "purple"},
    {value: "WARMUP", label: "Warm Up", color: "orange"},
    {value: "COOLDOWN", label: "Cool Down", color: "teal"},
];

export const typeMeta = (value) =>
    SEQUENCE_TYPES.find((t) => t.value === value) || {label: value, color: "gray"};

export const SAMPLE_SEQUENCES = [
    {
        id: "slow-march",
        name: "Slow March",
        type: "MARCH",
        icon: MdDirectionsWalk,
        description: "Steady parade-ground march beat for lining up and slow marching.",
        bpm: 60,
        steps: 8,
        defaultReps: 8,
        pattern: {
            kick: [2, 0, 0, 0, 1, 0, 0, 0],
            snare: [0, 0, 2, 0, 0, 0, 1, 0],
            hihat: [1, 1, 1, 1, 1, 1, 1, 1],
        },
    },
    {
        id: "quick-march",
        name: "Quick March",
        type: "MARCH",
        icon: MdMilitaryTech,
        description: "Brisk military quick-march cadence to keep the squad in step.",
        bpm: 112,
        steps: 8,
        defaultReps: 12,
        pattern: {
            kick: [2, 0, 1, 0, 2, 0, 1, 0],
            snare: [0, 0, 2, 0, 0, 0, 2, 0],
            hihat: [1, 1, 1, 1, 1, 1, 1, 1],
        },
    },
    {
        id: "double-time",
        name: "Double Time Run",
        type: "CARDIO",
        icon: MdDirectionsRun,
        description: "Fast running cadence for jogging drills and on-the-spot sprints.",
        bpm: 150,
        steps: 8,
        defaultReps: 16,
        pattern: {
            kick: [2, 1, 2, 1, 2, 1, 2, 1],
            snare: [0, 0, 2, 0, 0, 0, 2, 0],
            hihat: [1, 1, 1, 1, 1, 1, 1, 1],
        },
    },
    {
        id: "jumping-jacks",
        name: "Jumping Jacks",
        type: "CARDIO",
        icon: MdSportsGymnastics,
        description: "Bouncy 1-2 count with claps to keep jumping jacks in rhythm.",
        bpm: 120,
        steps: 8,
        defaultReps: 10,
        pattern: {
            kick: [2, 0, 0, 0, 2, 0, 0, 0],
            clap: [0, 0, 2, 0, 0, 0, 2, 0],
            hihat: [1, 1, 1, 1, 1, 1, 1, 1],
        },
    },
    {
        id: "attention-roll",
        name: "Attention Roll",
        type: "SIGNAL",
        icon: MdCampaign,
        description: "Rolling snare that builds to a crash — call the assembly to attention.",
        bpm: 180,
        steps: 8,
        defaultReps: 4,
        pattern: {
            snare: [1, 1, 1, 1, 1, 1, 1, 2],
            cymbal: [0, 0, 0, 0, 0, 0, 0, 2],
        },
    },
    {
        id: "whistle-cadence",
        name: "Whistle Cadence",
        type: "SIGNAL",
        icon: MdSportsScore,
        description: "Whistle blasts with a wood-block count for start / stop signals.",
        bpm: 80,
        steps: 8,
        defaultReps: 6,
        pattern: {
            whistle: [2, 0, 0, 0, 2, 0, 0, 0],
            wood: [0, 0, 1, 0, 0, 0, 1, 0],
        },
    },
    {
        id: "warmup-count",
        name: "Warm-Up Count",
        type: "WARMUP",
        icon: MdSelfImprovement,
        description: "Light wood-block 4-count for stretches and warm-up exercises.",
        bpm: 90,
        steps: 8,
        defaultReps: 8,
        pattern: {
            wood: [2, 1, 1, 1, 2, 1, 1, 1],
            hihat: [1, 0, 1, 0, 1, 0, 1, 0],
        },
    },
    {
        id: "cool-down",
        name: "Cool Down",
        type: "COOLDOWN",
        icon: MdSelfImprovement,
        description: "Slow, soft pulse for breathing and cool-down at the end of PT.",
        bpm: 55,
        steps: 8,
        defaultReps: 6,
        pattern: {
            kick: [2, 0, 0, 0, 0, 0, 0, 0],
            tom: [0, 0, 0, 0, 2, 0, 0, 0],
            hihat: [1, 0, 0, 1, 0, 0, 1, 0],
        },
    },
];

// Instruments actually used by a sequence, in the fixed INSTRUMENTS order so
// the step grid renders consistently.
export const sequenceInstruments = (sequence) => Object.keys(sequence.pattern || {});

// Total run time in seconds for the given tempo / repetitions. One step is one
// beat, so a cycle lasts `steps` beats and each beat is 60 / bpm seconds.
export const sequenceDuration = (sequence, bpm = sequence.bpm, reps = sequence.defaultReps) =>
    (sequence.steps * reps * 60) / Math.max(1, bpm);

export const formatDuration = (totalSeconds) => {
    const s = Math.round(totalSeconds || 0);
    const m = Math.floor(s / 60);
    const rem = s % 60;
    if (m === 0) return `${rem}s`;
    return rem === 0 ? `${m}m` : `${m}m ${rem}s`;
};
